// Demandes de documents — staff side of the Espace employé document
// workflow (attestations, ATS, fiches de paie…). Compact cg-island
// screen: open requests as rows with a one-click "advance" action
// (RECUE → PREPARATION → SIGNATURE → PRETE for originals, → ENVOYEE
// for scans), plus a processed list. No file storage — the document
// itself is emailed or handed over physically by the RH.
import { useState } from 'react';
import { useUiStore } from '../store/ui.js';
import { toast } from '../store/toast.js';
import {
  useDocRequests, useUpdateDocRequest,
  type AdminDocRequestDTO, type DocRequestStatus,
} from '../api/hooks.js';
import { CG_MONTHS, CgCount, cgAvColor, cgFmtLong, cgFmtRange, cgInitials, entityById } from '../components/conges/common.js';
import '../styles/conges.css';

const TYPE_LBL: Record<string, string> = {
  ATTESTATION_TRAVAIL: 'Attestation de travail',
  ATS: 'Attestation de salaire (ATS)',
  ATTESTATION_CONGE: 'Attestation de congé',
  FICHE_PAIE: 'Fiche de paie',
  AUTRE: 'Autre document',
};
const ST_LBL: Record<DocRequestStatus, [string, string]> = {
  RECUE: ['Reçue', 'var(--warns)|var(--warnc)'],
  PREPARATION: ['En préparation', 'var(--warns)|var(--warnc)'],
  SIGNATURE: ['En signature DG', 'var(--infos)|var(--infoc)'],
  PRETE: ['Prête à retirer', 'var(--greens)|var(--greenc)'],
  ENVOYEE: ['Envoyée', 'var(--greens)|var(--greenc)'],
  REFUSEE: ['Refusée', 'var(--bads)|var(--badc)'],
};
const nextStatus = (d: AdminDocRequestDTO): DocRequestStatus | null => {
  if (d.status === 'RECUE') return 'PREPARATION';
  if (d.status === 'PREPARATION') return d.forme === 'ORIGINAL' ? 'SIGNATURE' : 'ENVOYEE';
  if (d.status === 'SIGNATURE') return 'PRETE';
  return null;
};
const NEXT_LBL: Record<string, string> = {
  PREPARATION: 'Démarrer la préparation',
  SIGNATURE: 'Transmettre au DG',
  PRETE: 'Marquer prête à retirer',
  ENVOYEE: 'Marquer envoyée par email',
};
const monthsLbl = (months: string[]) => months.map((k) => CG_MONTHS[Number(k.slice(-2)) - 1]).join(', ');

export function DocumentsAdminPage() {
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const entityId = useUiStore((s) => s.currentEntityId);
  const [scope, setScope] = useState<'open' | 'done'>('open');
  const list = useDocRequests({ entityId, scope });
  const update = useUpdateDocRequest();

  const act = (d: AdminDocRequestDTO, status: DocRequestStatus, msg: string) =>
    update.mutate({ id: d.id, status }, {
      onSuccess: () => toast.success(msg),
      onError: () => toast.danger('Échec — réessayez.'),
    });

  const rows = list.data ?? [];

  return (
    <div className={'cgapp' + (themeDark ? ' theme-dark' : '')}>
      <div className="cg-scrollpage">
        <div className="gm-wrap">
          <div className="gm-eyebrow">Données · Documents</div>
          <h1 className="gm-title">Demandes de <em>documents</em></h1>
          <p className="gm-sub">
            Attestations, ATS et fiches de paie demandées depuis l'Espace employé. Faites avancer chaque
            demande jusqu'à la remise — copie électronique envoyée par email ou original signé DG à retirer.
          </p>

          <div className="cg-ctrl">
            <div className="cg-seg">
              {([['open', 'À traiter'], ['done', 'Traitées']] as const).map(([k, l]) => (
                <button key={k} className={scope === k ? 'on' : ''} onClick={() => setScope(k)}>
                  {l}
                  {k === 'open' && scope === 'open' && rows.length > 0 && <span className="bdg">{rows.length}</span>}
                </button>
              ))}
            </div>
            <div className="cg-spacer" />
            <div className="cg-kpi" style={{ padding: '8px 14px' }}>
              <span className="cg-kpi-v accent" style={{ fontSize: 18 }}><CgCount value={rows.length} dec={0} /></span>
              <span className="cg-kpi-l">{scope === 'open' ? 'demandes ouvertes' : 'demandes traitées'}</span>
            </div>
          </div>

          <div className="gm-gridwrap view-enter">
            <div className="gm-scroll">
              <table className="sb-tbl">
                <thead>
                  <tr>
                    <th className="sb-cEmp">Employé</th>
                    <th>Document</th>
                    <th>Forme</th>
                    <th>Détails</th>
                    <th>Statut</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d, i) => {
                    const ent = entityById(d.employee.entityId);
                    const [stLbl, tone] = ST_LBL[d.status];
                    const [bg, fg] = tone.split('|');
                    const next = nextStatus(d);
                    return (
                      <tr key={d.id} className="cg-row" style={{ animationDelay: `${i * 18}ms`, cursor: 'default' }}>
                        <td className="sb-cEmp">
                          <div className="gm-emp">
                            <span className="gm-av" style={{ background: cgAvColor(d.employee.id) }}>{cgInitials(d.employee.name)}</span>
                            <span className="gm-empmeta">
                              <span className="gm-name">{d.employee.name}</span>
                              <span className="gm-role">
                                {ent && <i className="cg-entdot" style={{ background: ent.color }} />}
                                {ent?.short ?? d.employee.entityId} · {cgFmtLong(d.createdAt.slice(0, 10))}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td style={{ fontWeight: 700, fontSize: 12.5 }}>
                          {TYPE_LBL[d.type] ?? d.type}
                          {d.months.length > 0 && <div style={{ fontWeight: 500, fontSize: 11, color: 'var(--ink3)' }}>{monthsLbl(d.months)}</div>}
                          {d.leaveStart && d.leaveEnd && <div style={{ fontWeight: 500, fontSize: 11, color: 'var(--ink3)' }}>{cgFmtRange(d.leaveStart, d.leaveEnd)}</div>}
                          {d.salaryMonths && <div style={{ fontWeight: 500, fontSize: 11, color: 'var(--ink3)' }}>{d.salaryMonths} derniers mois</div>}
                        </td>
                        <td style={{ fontSize: 12 }}>{d.forme === 'ORIGINAL' ? 'Original signé DG' : 'Copie électronique'}<div style={{ fontSize: 11, color: 'var(--ink3)' }}>{d.copies} ex. · {d.lang}</div></td>
                        <td style={{ fontSize: 12, maxWidth: 260 }}>
                          {d.dest && <div>{d.dest}</div>}
                          {d.note && <div style={{ color: 'var(--ink3)', fontStyle: 'italic' }}>« {d.note} »</div>}
                          {!d.dest && !d.note && <span className="dim">—</span>}
                        </td>
                        <td>
                          <span className="cg-typechip" style={{ background: bg, color: fg }}>{stLbl}</span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {next && (
                            <button className="gm-attn-go" style={{ padding: '8px 13px', fontSize: 11.5 }} disabled={update.isPending}
                              onClick={() => act(d, next, ST_LBL[next][0])}>
                              {NEXT_LBL[next]}
                            </button>
                          )}
                          {next && d.status === 'RECUE' && (
                            <button className="cg-ghost" style={{ marginLeft: 7, padding: '8px 12px', fontSize: 11.5 }} disabled={update.isPending}
                              onClick={() => act(d, 'REFUSEE', 'Demande refusée')}>
                              Refuser
                            </button>
                          )}
                          {!next && <span className="dim" style={{ fontSize: 11.5 }}>{d.doneAt ? cgFmtLong(d.doneAt.slice(0, 10)) : '—'}</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={6}>
                      <div className="cg-empty" style={{ margin: 14 }}>
                        {list.isLoading ? 'Chargement…' : scope === 'open' ? '🎉 Aucune demande à traiter.' : 'Aucune demande traitée.'}
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
